import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { AccountForm } from "./forms/AccountForm";
import { HotelDetailsForm } from "./forms/HotelDetailsForm";
import { PMSCredentialsForm } from "./forms/PMSCredentialsForm";
import {
  safeBase64Encode,
  Connector,
  Credentials,
} from "../shared/ConnectorCredentialsForm";
import { CreateSftpAccountDto, SftpAccountDto } from "../../types/sftp";
import { Copy, Check } from "lucide-react";
import { getDefaultPmsBaseUrl } from "../../lib/pmsDefaultBaseUrl";

interface OnboardingConfig {
  logo_url?: string;
  font_family?: string;
  primary_color?: string;
  secondary_color?: string;
}

// Helper function to generate a random 5-letter code
const generateHotelCode = (): string => {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  return Array.from({ length: 5 }, () =>
    letters.charAt(Math.floor(Math.random() * letters.length))
  ).join("");
};

// Generate secure random password
const generateRandomPassword = (): string => {
  const length = 16;
  const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
  const values = new Uint8Array(length);
  crypto.getRandomValues(values);
  return Array.from(values, (x) => charset[x % charset.length]).join("");
};

// Slugify a SaaS company name into something safe for an email local-part.
// Strips diacritics, lowercases, replaces non-[a-z0-9]+ with "_", trims edges.
const slugifyForEmail = (value: string): string => {
  return (value || "saas")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "saas";
};

export function SaasHotelOnboardingWidget() {
  const { saasId } = useParams<{ saasId: string }>();
  const [searchParams] = useSearchParams();
  const noHotelAccount = searchParams.get("mode") === "no_hotel_account";
  const [currentStep, setCurrentStep] = useState<number>(noHotelAccount ? 1 : 0);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [selectedConnector, setSelectedConnector] = useState<Connector | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [onboardingConfig, setOnboardingConfig] = useState<OnboardingConfig | null>(null);
  const [saasScopes, setSaasScopes] = useState<string[]>([]);
  const [companyName, setCompanyName] = useState<string>("");
  const [generatedPassword, setGeneratedPassword] = useState<string>("");
  const [passwordCopied, setPasswordCopied] = useState(false);

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [hotelDetails, setHotelDetails] = useState({
    hotel_name: "",
    category: "",
    website: "",
    contact_phone: "",
    address: "",
    postal_code: "",
    city: "",
    country: "",
    registration_number: "",
  });
  const [credentials, setCredentials] = useState<Credentials>({});
  const [hotelCode, setHotelCode] = useState("");
  const [createdUserId, setCreatedUserId] = useState<string | null>(null);

  useEffect(() => {
    if (saasId) {
      loadOnboardingConfig();
    }
  }, [saasId]);

  useEffect(() => {
    if (currentStep === 2) {
      loadConnectors();
    }
  }, [currentStep]);

  const loadOnboardingConfig = async () => {
    try {
      if (!saasId) {
        setError("Invalid SaaS ID");
        return;
      }

      const { data, error: queryError } = await supabase
        .from("saas_onboarding_config")
        .select("company_name, onboarding_config, scopes")
        .eq("id", saasId)
        .maybeSingle();

      if (queryError) {
        throw queryError;
      }

      if (!data) {
        setError("SaaS provider not found");
        return;
      }

      setOnboardingConfig((data.onboarding_config as OnboardingConfig) || null);
      setSaasScopes(data.scopes || []);
      setCompanyName((data as { company_name?: string }).company_name || "");
    } catch (err) {
      console.error("Error loading onboarding config:", err);
      setError("Failed to load onboarding configuration");
    }
  };

  const loadConnectors = async () => {
    try {
      setLoading(true);
      const { data, error: queryError } = await supabase
        .from("connectors")
        .select("id, code, name, type, status, cloudified, credentials")
        .eq("type", "pms")
        .eq("status", "active")
        .order("name");

      if (queryError) throw queryError;
      setConnectors(data || []);
    } catch (err) {
      console.error("Error loading connectors:", err);
      setError("Failed to load PMS providers");
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    // ALL_STEPS / firstStepIndex are declared further down but only used at render
    // time; we hard-code the upper bound (4 logical steps) here to keep this in
    // hoist-safe territory.
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    const lowerBound = noHotelAccount ? 1 : 0;
    if (currentStep > lowerBound) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleConnectorSelect = (connector: Connector) => {
    setSelectedConnector(connector);
    // Most hotels onboarding through the widget only configure a single connector,
    // so we default "Set as Primary Connector" to true. Users can still uncheck it.
    const initialCredentials: Credentials = {
      is_primary: true,
    };

    if (connector.credentials && connector.credentials.length > 0) {
      connector.credentials.forEach((field) => {
        initialCredentials[field.key] = "";
      });
    }

    const defaultBaseUrl = getDefaultPmsBaseUrl(connector);
    if (defaultBaseUrl) {
      initialCredentials.base_url = defaultBaseUrl;
    }

    setCredentials(initialCredentials);
  };

  const handleCredentialChange = (key: string, value: string | boolean) => {
    setCredentials((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Helper kept commented while the SIRET validation is disabled — restore alongside the validation block in handleHotelCreation.
  // const validateSIRET = (siret: string): boolean => {
  //   return /^\d{14}$/.test(siret);
  // };

  /**
   * Provisions a brand-new auth user and `profiles` row for widget onboarding.
   *
   * Used by both:
   *   - the standard 4-step flow (called from handleUserCreation on Step 0 submit)
   *   - the no_hotel_account mode (called inline from handleHotelCreation with
   *     synthetic credentials derived from the SaaS company_name + hotel_code)
   *
   * The generated password is stored in state via setGeneratedPassword so that
   * the standard flow can display it on the final "Setup Complete" step. The
   * no_hotel_account flow hides that section.
   */
  const provisionWidgetUser = async (opts: {
    firstName: string;
    lastName: string;
    email: string;
  }): Promise<{ userId: string }> => {
    const { firstName: fName, lastName: lName, email: targetEmail } = opts;

    if (!fName.trim() || !lName.trim()) {
      throw new Error("First name and last name are required");
    }

    // Check if email is already registered
    const { data: existingProfile } = await supabase
      .from("profiles")
      .select("id")
      .eq("email", targetEmail)
      .maybeSingle();

    if (existingProfile) {
      throw new Error("An account with this email already exists.");
    }

    const password = generateRandomPassword();
    setGeneratedPassword(password);

    const { data: authData, error: signUpError } = await supabase.auth.signUp({
      email: targetEmail,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/extranet/hotel`,
        data: {
          first_name: fName,
          last_name: lName,
          user_type: "hotel",
        },
      },
    });

    if (signUpError) {
      if (
        signUpError.message.includes("already registered") ||
        signUpError.message.includes("already been registered")
      ) {
        throw new Error("An account with this email already exists.");
      }
      throw signUpError;
    }

    if (!authData.user) {
      throw new Error("Failed to create user account");
    }

    // Create profile - try multiple approaches
    let profileCreated = false;
    const profileCreationErrors: string[] = [];
    const fullName = `${fName} ${lName}`;
    const profileData = {
      id: authData.user.id,
      email: targetEmail,
      user_type: "hotel",
      full_name: fullName,
    };

    // Approach 1: If session exists, try direct insert
    if (authData.session) {
      console.log("Attempting Approach 1: Direct insert with session");
      const { error: directError } = await supabase
        .from("profiles")
        .insert([profileData]);
      if (!directError) {
        console.log("Approach 1 succeeded: Profile created via direct insert");
        profileCreated = true;
      } else {
        console.error("Approach 1 failed:", directError);
        profileCreationErrors.push(`Direct insert: ${directError.message}`);
      }
    } else {
      console.log(
        "Approach 1 skipped: No session available (email confirmation may be required)"
      );
    }

    // Approach 2: If no session, try to sign in first, then create profile
    if (!profileCreated && !authData.session) {
      console.log("Attempting Approach 2: Sign in then create profile");
      const { data: signInData, error: signInError } =
        await supabase.auth.signInWithPassword({
          email: targetEmail,
          password,
        });

      if (!signInError && signInData.session) {
        const { error: profileError } = await supabase
          .from("profiles")
          .insert([profileData]);
        if (!profileError) {
          console.log("Approach 2 succeeded: Profile created after sign in");
          profileCreated = true;
        } else {
          console.error(
            "Approach 2 failed: Profile insert after sign in failed:",
            profileError
          );
          profileCreationErrors.push(
            `Sign in + insert: ${profileError.message}`
          );
        }
      } else {
        console.error(
          "Approach 2 failed: Could not sign in:",
          signInError?.message || "Unknown error"
        );
        profileCreationErrors.push(
          `Sign in: ${signInError?.message || "Email confirmation may be required"}`
        );
      }
    }

    // Approach 3: Try RPC function (if migration has been applied)
    if (!profileCreated) {
      console.log(
        "Attempting Approach 3: RPC function create_profile_for_widget"
      );
      const { error: rpcError } = await supabase.rpc(
        "create_profile_for_widget",
        {
          user_id: authData.user.id,
          user_email: targetEmail,
          user_type: "hotel",
          full_name: fullName,
        }
      );

      if (!rpcError) {
        console.log("Approach 3 succeeded: Profile created via RPC function");
        profileCreated = true;
      } else {
        console.error("Approach 3 failed - RPC function error:", {
          message: rpcError.message,
          details: rpcError.details,
          hint: rpcError.hint,
          code: rpcError.code,
        });

        let errorMessage = rpcError.message;
        if (rpcError.message.includes("Unauthorized")) {
          errorMessage = `RPC authorization failed: ${rpcError.message}. The user may have been created too long ago or the security check failed.`;
        } else if (
          rpcError.message.includes("does not exist") ||
          rpcError.code === "42883"
        ) {
          errorMessage = `RPC function not found: The database migration may not have been applied. Please contact support.`;
        } else if (rpcError.message.includes("User does not exist")) {
          errorMessage = `RPC error: User was not found in auth.users. This may indicate a timing issue.`;
        }

        profileCreationErrors.push(`RPC function: ${errorMessage}`);
      }
    }

    if (!profileCreated) {
      const errorDetails =
        profileCreationErrors.length > 0
          ? `\n\nAttempted methods:\n${profileCreationErrors
              .map((err, idx) => `${idx + 1}. ${err}`)
              .join("\n")}`
          : "";

      throw new Error(
        `Failed to create user profile. Please ensure email confirmation is disabled or contact support.${errorDetails}`
      );
    }

    return { userId: authData.user.id };
  };

  const handleUserCreation = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (!saasId) {
        throw new Error("Invalid SaaS ID");
      }

      const { userId } = await provisionWidgetUser({ firstName, lastName, email });
      setCreatedUserId(userId);

      handleNext();
    } catch (err: any) {
      console.error("Signup error:", err);
      setError(err.message || "An error occurred during signup");
    } finally {
      setLoading(false);
    }
  };

  const handleHotelCreation = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (!saasId) {
        throw new Error("Invalid SaaS ID");
      }

      // Registration number (SIRET) validation temporarily disabled — kept here so it can be restored later.
      // if (
      //   hotelDetails.country === "France" &&
      //   !validateSIRET(hotelDetails.registration_number)
      // ) {
      //   setError(
      //     "For France, registration number (SIRET) must be exactly 14 digits"
      //   );
      //   return;
      // }

      // Use the stored user ID from step 1, or fallback to current authenticated user.
      // In no_hotel_account mode the user has not been created yet — we will mint
      // a synthetic auth user inline (right after generating the hotel_code).
      let userId = createdUserId;
      let effectiveEmail = email;

      if (!userId && !noHotelAccount) {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error("No authenticated user found");
        userId = user.id;
      }

      // Compute hotel_code first. In no_hotel_account mode it feeds the synthetic
      // email; in the standard flow it just gets reused as before. When userId is
      // not known yet (no_hotel_account first pass) we skip the "existing hotel"
      // lookup and generate a fresh code.
      let existingHotelCode: string | null = null;
      if (userId) {
        const { data: existingHotel } = await supabase
          .from("hotel_details")
          .select("hotel_code")
          .eq("id", userId)
          .maybeSingle();
        existingHotelCode = existingHotel?.hotel_code
          ? String(existingHotel.hotel_code)
          : null;
      }
      const hotelCodeValue = existingHotelCode || generateHotelCode();
      setHotelCode(hotelCodeValue);

      // no_hotel_account mode: provision a synthetic user now that we have a hotel_code.
      if (!userId && noHotelAccount) {
        const slug = slugifyForEmail(companyName);
        const syntheticEmail = `Onboarding_${slug}_${hotelCodeValue}@yopmail.com`;
        const provisioned = await provisionWidgetUser({
          firstName: "Onboarding",
          lastName: companyName || "SaaS",
          email: syntheticEmail,
        });
        userId = provisioned.userId;
        effectiveEmail = syntheticEmail;
        setCreatedUserId(userId);
        setEmail(syntheticEmail);
      }

      // Prepare hotel data
      const hotelData: any = {
        id: userId,
        hotel_name: hotelDetails.hotel_name,
        category: hotelDetails.category,
        website: hotelDetails.website,
        address: hotelDetails.address,
        postal_code: hotelDetails.postal_code,
        city: hotelDetails.city,
        country: hotelDetails.country,
        registration_number: hotelDetails.registration_number?.trim() || null,
        contact_email: effectiveEmail,
        contact_phone: hotelDetails.contact_phone,
        hotel_code: hotelCodeValue,
        updated_at: new Date().toISOString(),
      };

      // Always use the widget RPC so hotel_details creation works regardless of
      // country, session, or who is logged in (SaaS vs newly created hotel user).
      // Direct upsert requires auth.uid() = id and bypasses server-side normalization.
      const { error: rpcError } = await supabase.rpc('upsert_hotel_details_for_widget', {
        user_id: userId,
        hotel_name: hotelData.hotel_name,
        category: hotelData.category,
        website: hotelData.website || null,
        address: hotelData.address,
        postal_code: hotelData.postal_code,
        city: hotelData.city,
        country: hotelData.country,
        registration_number: hotelData.registration_number,
        contact_email: hotelData.contact_email,
        contact_phone: hotelData.contact_phone,
        hotel_code: hotelData.hotel_code,
      });

      if (rpcError) throw rpcError;

      // Always go through create_integration_for_widget so that:
      //   - onboarded_via_widget = true is set even if the row already exists, and
      //   - scopes are sourced from saas_details on the server.
      const { error: integrationRpcError } = await supabase.rpc(
        "create_integration_for_widget",
        {
          hotel_id: userId,
          saas_id: saasId,
          status: "active",
          scopes: saasScopes || [],
        }
      );

      if (integrationRpcError) throw integrationRpcError;

      // Move to next step
      handleNext();
    } catch (err: any) {
      console.error("Hotel details error:", err);
      setError(err.message || "Failed to save hotel details");
    } finally {
      setLoading(false);
    }
  };

  const handlePMSSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Use the stored user ID from step 1, or fallback to current authenticated user
      let userId = createdUserId;
      if (!userId) {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error("No authenticated user found");
        userId = user.id;
      }

      // Check if user is authenticated (has session)
      const { data: { session } } = await supabase.auth.getSession();

      if (!selectedConnector) {
        throw new Error("Please select a PMS provider");
      }

      if (!credentials.connector_hotel_id) {
        throw new Error("Hotel Identifier is required");
      }

      if (selectedConnector.cloudified) {
        const hotelId =
          typeof credentials.connector_hotel_id === "string"
            ? credentials.connector_hotel_id
            : "";

        const createSftpDto: CreateSftpAccountDto = {
          hotelId: hotelCode.toLowerCase(),
        };

        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "";
        const response = await fetch(`${apiBaseUrl}/v1/sftp/accounts`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(createSftpDto),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to create SFTP account");
        }

        const sftpAccount: SftpAccountDto = await response.json();
        const connector_credentials = {
          sftp: {
            username: sftpAccount.username,
            password: sftpAccount.password,
            url: sftpAccount.url,
            port: sftpAccount.port,
          },
        };

        let encodedCredentials;
        try {
          encodedCredentials = safeBase64Encode(connector_credentials);
        } catch (encodeError) {
          throw new Error("Failed to process SFTP credentials. Please try again.");
        }

        const credentialData = {
          hotel_id: userId,
          connector_type: "pms",
          base_url: "",
          connector_code: selectedConnector.code,
          connector_hotel_id: hotelId,
          is_primary: credentials.is_primary === true,
          reqParams: encodedCredentials,
          updated_at: new Date().toISOString(),
        };

        // If no session (anonymous/incognito), use RPC function to bypass RLS
        if (!session) {
          const { error: rpcError } = await supabase.rpc('create_credentials_for_widget', {
            hotel_id: userId,
            connector_type: credentialData.connector_type,
            connector_code: credentialData.connector_code,
            connector_hotel_id: credentialData.connector_hotel_id,
            base_url: credentialData.base_url,
            is_primary: credentialData.is_primary,
            reqParams: credentialData.reqParams,
          });
          
          if (rpcError) throw rpcError;
        } else {
          const { error: credentialsError } = await supabase
            .from("credentials")
            .insert([credentialData]);

          if (credentialsError) throw credentialsError;
        }
      } else {
        if (!credentials.base_url) {
          throw new Error("Connector URL is required");
        }

        try {
          const baseUrl =
            typeof credentials.base_url === "string"
              ? credentials.base_url
              : "";
          new URL(baseUrl);
        } catch (e) {
          throw new Error("Please enter a valid Connector URL");
        }

        const missingFields =
          selectedConnector.credentials &&
          selectedConnector.credentials.length > 0
            ? selectedConnector.credentials
                .filter((field) => !credentials[field.key])
                .map((field) => field.key)
            : [];

        if (missingFields.length > 0) {
          throw new Error(
            `Missing required fields: ${missingFields.join(", ")}`
          );
        }

        const connector_credentials = {
          credentials: { ...credentials },
        };

        delete connector_credentials.credentials.connector_hotel_id;
        delete connector_credentials.credentials.base_url;
        delete connector_credentials.credentials.is_primary;

        let encodedCredentials;
        try {
          encodedCredentials = safeBase64Encode(connector_credentials);
        } catch (encodeError) {
          throw new Error("Failed to process credentials. Please try again.");
        }

        const credentialData = {
          hotel_id: userId,
          connector_type: "pms",
          base_url:
            typeof credentials.base_url === "string"
              ? credentials.base_url
              : "",
          connector_code: selectedConnector.code,
          connector_hotel_id:
            typeof credentials.connector_hotel_id === "string"
              ? credentials.connector_hotel_id
              : "",
          is_primary: credentials.is_primary === true,
          reqParams: encodedCredentials,
          updated_at: new Date().toISOString(),
        };

        // If no session (anonymous/incognito), use RPC function to bypass RLS
        // Note: PostgreSQL converts parameter names to lowercase, so use reqparams (not reqParams)
        if (!session) {
          const { error: rpcError } = await supabase.rpc('create_credentials_for_widget', {
            hotel_id: userId,
            connector_type: credentialData.connector_type,
            connector_code: credentialData.connector_code,
            connector_hotel_id: credentialData.connector_hotel_id,
            base_url: credentialData.base_url,
            is_primary: credentialData.is_primary,
            reqparams: credentialData.reqParams, // Use lowercase for PostgreSQL
          });
          
          if (rpcError) throw rpcError;
        } else {
          const { error: credentialsError } = await supabase
            .from("credentials")
            .insert([credentialData]);

          if (credentialsError) throw credentialsError;
        }
      }

      // Move to completion step
      handleNext();
    } catch (err: any) {
      console.error("PMS setup error:", err);
      setError(err.message || "Failed to save Connector credentials");
    } finally {
      setLoading(false);
    }
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(generatedPassword);
      setPasswordCopied(true);
      setTimeout(() => setPasswordCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy password:", err);
    }
  };

  // "Logical" steps: indices map 1:1 to the values of `currentStep`, which the
  // submit handlers rely on. The first entry (Account) is hidden in
  // no_hotel_account mode but indices stay aligned so handleHotelCreation /
  // handlePMSSetup keep firing at currentStep === 1 / 2.
  const ALL_STEPS = [
    {
      title: "Create Your Account",
      description: "Set up your personal account to get started.",
    },
    {
      title: "Hotel Details",
      description: "Tell us about your hotel so we can better serve your needs.",
    },
    {
      title: "Connector Integration",
      description: "Connect your Connector Management System (e.g. PMS) to enable seamless integration.",
    },
    {
      title: "Setup Complete",
      description: noHotelAccount
        ? "Your hotel has been connected successfully."
        : "Your account has been created successfully.",
    },
  ];
  // What the stepper displays. In no_hotel_account mode we hide step 0.
  const visibleSteps = noHotelAccount ? ALL_STEPS.slice(1) : ALL_STEPS;
  const firstStepIndex = noHotelAccount ? 1 : 0;
  // currentStep is a logical index into ALL_STEPS. Translate to visible index for the progress bar.
  const visibleCurrentStep = currentStep - firstStepIndex;

  // Apply custom styling
  const customStyles: React.CSSProperties = {
    fontFamily: onboardingConfig?.font_family || "Inter, sans-serif",
    "--primary-color": onboardingConfig?.primary_color || "#8B5CF6",
    "--secondary-color": onboardingConfig?.secondary_color || "#9D72F7",
  } as React.CSSProperties;

  const primaryColor = onboardingConfig?.primary_color || "#8B5CF6";
  const secondaryColor = onboardingConfig?.secondary_color || "#9D72F7";

  return (
    <div className="min-h-screen flex flex-col" style={customStyles}>
      <div className="w-full bg-white p-4 md:p-8 flex flex-col relative flex-1">
        {/* Logo */}
        {onboardingConfig?.logo_url && (
          <div className="mb-8 flex justify-center">
            <img
              src={onboardingConfig.logo_url}
              alt="Logo"
              className="max-h-16 max-w-full object-contain"
            />
          </div>
        )}

        <div className="flex-1 max-w-2xl mx-auto w-full">
          {/* Progress indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {visibleSteps.map((step, index) => (
                <div
                  key={index}
                  className={`flex items-center ${
                    index < visibleSteps.length - 1 ? "flex-1" : ""
                  }`}
                >
                  <div
                    className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                      ${
                        index <= visibleCurrentStep
                          ? "text-white"
                          : "bg-gray-200 text-gray-500"
                      }
                    `}
                    style={
                      index <= visibleCurrentStep
                        ? { backgroundColor: primaryColor }
                        : undefined
                    }
                  >
                    {index + 1}
                  </div>
                  {index < visibleSteps.length - 1 && (
                    <div
                      className={`
                        flex-1 h-0.5 mx-4
                        ${index < visibleCurrentStep ? "" : "bg-gray-200"}
                      `}
                      style={
                        index < visibleCurrentStep
                          ? { backgroundColor: primaryColor }
                          : undefined
                      }
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Step content */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {ALL_STEPS[currentStep].title}
            </h2>
            <p className="text-gray-500">{ALL_STEPS[currentStep].description}</p>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {currentStep === 0 && (
            <AccountForm
              email={email}
              setEmail={setEmail}
              password=""
              setPassword={() => {}}
              firstName={firstName}
              setFirstName={setFirstName}
              lastName={lastName}
              setLastName={setLastName}
              hidePassword={true}
            />
          )}

          {currentStep === 1 && (
            <HotelDetailsForm
              hotelDetails={hotelDetails}
              setHotelDetails={setHotelDetails}
            />
          )}

          {currentStep === 2 && (
            <PMSCredentialsForm
              connectors={connectors}
              selectedConnector={selectedConnector}
              credentials={credentials}
              onConnectorSelect={handleConnectorSelect}
              onCredentialChange={handleCredentialChange}
            />
          )}

          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="p-6 bg-green-50 border border-green-200 rounded-md">
                <h3 className="text-lg font-semibold text-green-800 mb-2">
                  {noHotelAccount ? "Hotel Connected Successfully!" : "Account Created Successfully!"}
                </h3>
                <p className="text-green-700">
                  {noHotelAccount
                    ? "Your hotel has been connected to the SaaS provider. The SaaS team will manage this integration on your behalf from their extranet."
                    : "Your hotel account has been created and is now connected to the SaaS provider."}
                </p>
              </div>

              {!noHotelAccount && (
                <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-md">
                  <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                    Important: Save Your Password
                  </h3>
                  <p className="text-yellow-700 mb-4">
                    Your account password has been generated. Please copy it now as it will not be shown again.
                  </p>
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 p-3 bg-white border border-yellow-300 rounded-md font-mono text-sm">
                      {generatedPassword}
                    </div>
                    <button
                      type="button"
                      onClick={copyPassword}
                      className="px-4 py-2 rounded-md text-white font-medium flex items-center space-x-2"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {passwordCopied ? (
                        <>
                          <Check className="h-4 w-4" />
                          <span>Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              <div className="p-6 bg-blue-50 border border-blue-200 rounded-md">
                <h3 className="text-lg font-semibold text-blue-800 mb-2">
                  Next Steps
                </h3>
                <ul className="list-disc list-inside text-blue-700 space-y-2">
                  {noHotelAccount ? (
                    <>
                      <li>Your integration is already active and ready to use</li>
                      <li>You can close this window — there is nothing else for you to do</li>
                      <li>Contact your SaaS provider if you need to update connection details</li>
                    </>
                  ) : (
                    <>
                      <li>Log in to your hotel dashboard using your email and the password above</li>
                      <li>Your integration is already active and ready to use</li>
                      <li>You can change your password from the settings page after logging in</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          )}

          {currentStep < 3 && (
            <div className="mt-8 flex justify-between">
              <button
                type="button"
                onClick={handleBack}
                disabled={currentStep <= firstStepIndex}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 rounded-md text-white font-medium"
                style={{ backgroundColor: primaryColor }}
                onClick={
                  currentStep === 0
                    ? handleUserCreation
                    : currentStep === 1
                    ? handleHotelCreation
                    : handlePMSSetup
                }
              >
                {loading ? "Processing..." : currentStep < ALL_STEPS.length - 2 ? "Next" : "Complete Setup"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
